import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import toast from 'react-hot-toast';
import {
    IconUpload, IconDownload, IconEdit, IconTrash, IconSearch,
    IconFile, IconAlertTriangle, IconLoader, IconFileText,
    IconLock, IconUsers, IconBuilding, IconHistory, IconChevronLeft, IconX,
} from '@tabler/icons-react';
import {
    useDocuments, useDeleteDocument, useDownloadDocument, useExpiringDocuments,
    useDocumentAccessLog,
} from '../../hooks/useDocuments';
import { useEmployee } from '../../hooks/useEmployees';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = [
    { value: 'cv_resume',    label: 'CV / Résumé'                  },
    { value: 'cover_letter', label: 'Cover Letter'                  },
    { value: 'certificate',  label: 'Certificate / Qualification'   },
    { value: 'id_document',  label: 'ID Document'                   },
    { value: 'contract',     label: 'Employment Contract'           },
    { value: 'appraisal',    label: 'Appraisal / Review'            },
    { value: 'training',     label: 'Training Record'               },
    { value: 'office_memo',  label: 'Office Memo / Correspondence'  },
    { value: 'policy',       label: 'Policy / Compliance Document'  },
    { value: 'personal',     label: 'Personal'                      },
    { value: 'other',        label: 'Other'                         },
];

const VISIBILITY_ICONS = {
    private:      IconLock,
    line_manager: IconUsers,
    org:          IconBuilding,
};

const MIME_ICON_COLOR = {
    'application/pdf': 'text-danger',
    'image/jpeg':      'text-info',
    'image/png':       'text-info',
    'image/webp':      'text-info',
};

function formatSize(bytes) {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function categoryLabel(value) {
    return CATEGORIES.find(c => c.value === value)?.label || value;
}

function AccessLogModal({ doc, onClose }) {
    const { data, isLoading } = useDocumentAccessLog(doc.id);
    const logs = data?.logs || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-15">
            <div className="bg-bg-dark rounded-xl border border-border shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-20 py-15 border-b border-border flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-[14px] font-bold text-text-primary truncate">Access Log</h3>
                        <p className="text-xs text-text-secondary truncate">{doc.title}</p>
                    </div>
                    <button onClick={onClose} className="w-28 h-28 rounded-lg flex items-center justify-center text-text-secondary hover:bg-bg-light flex-shrink-0">
                        <IconX size={16} />
                    </button>
                </div>
                <div className="overflow-auto p-15 flex-grow">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-30 text-text-secondary">
                            <IconLoader className="animate-spin" size={18} />
                        </div>
                    ) : logs.length > 0 ? (
                        <div className="flex flex-col gap-10">
                            {logs.map((log) => (
                                <div key={log.id} className="flex items-center justify-between gap-10 px-12 py-10 bg-bg-light rounded-lg">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-text-primary truncate">{log.accessed_by_name}</p>
                                        <p className="text-xs text-text-secondary truncate">{log.ip_address}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-6 py-2 rounded-full bg-primary/10 text-primary">
                                            {log.action}
                                        </span>
                                        <p className="text-[10px] text-text-secondary mt-4">{formatDate(log.accessed_at)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-text-secondary text-center py-30">No access activity recorded yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function EmployeeDocuments() {
    const navigate      = useNavigate();
    const { employeeId } = useParams();
    const { hasAnyPermission, user } = useAuth();
    const isHR          = !!user?.is_superuser || hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);
    const orgId         = user?.orgId;

    const { data: employee } = useEmployee(employeeId, orgId);

    const [search, setSearch]           = useState('');
    const [category, setCategory]       = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [accessLogDoc, setAccessLogDoc]   = useState(null);

    const { data: docs = [], isLoading } = useDocuments({
        search, category, ordering: '-created_at',
        ...(employeeId && { employee_id: employeeId }),
    });

    const { data: expiringData } = useExpiringDocuments(30);
    const expiringCount = employeeId ? 0 : (expiringData?.count || 0);

    const deleteDoc   = useDeleteDocument();
    const downloadDoc = useDownloadDocument();

    const handleDelete = async () => {
        try {
            await deleteDoc.mutateAsync(confirmDelete.id);
            toast.success('Document deleted.');
            setConfirmDelete(null);
        } catch {}
    };

    const handleDownload = (doc) => {
        downloadDoc.mutate({ id: doc.id, filename: doc.original_filename || doc.title });
    };

    const uploadLink = employeeId ? `/documents/upload?employee_id=${employeeId}` : '/documents/upload';

    if (isLoading) return <PageLoader text="Loading documents..." />;

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title={employeeId ? `${employee?.full_name || 'Employee'}'s Documents` : 'My Documents'} path="Documents" />

            {employeeId && (
                <button onClick={() => navigate(`/employee/${employeeId}/snapshot`)} className="btn btn-outline-secondary self-start">
                    <IconChevronLeft size={15} /> Back to Snapshot
                </button>
            )}

            {/* Expiry banner */}
            {expiringCount > 0 && (
                <div className="flex items-center gap-10 px-15 py-10 bg-warning/10 border border-warning/30 rounded-lg">
                    <IconAlertTriangle size={15} className="text-warning flex-shrink-0" />
                    <p className="text-[12px] text-text-primary">
                        <span className="font-bold">{expiringCount} document{expiringCount !== 1 ? 's' : ''}</span> expiring in the next 30 days.
                    </p>
                </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-10">
                <div className="form-group relative flex-1 min-w-[200px]">
                    <IconSearch size={13} className="absolute top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" style={{ left: '10px' }} />
                    <input
                        type="text"
                        placeholder="Search documents..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ paddingLeft: '28px' }}
                    />
                </div>
                <div className="form-group min-w-[180px]">
                    <select value={category} onChange={e => setCategory(e.target.value)}>
                        <option value="">All Categories</option>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <button onClick={() => navigate(uploadLink)} className="btn btn-primary">
                    <IconUpload size={14} /> Upload
                </button>
            </div>

            {/* List */}
            {docs.length === 0 ? (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light py-60 text-center">
                    <div className="w-44 h-44 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-12">
                        <IconFileText className="text-primary" size={20} />
                    </div>
                    <p className="text-sm font-semibold text-text-primary mb-4">No documents yet</p>
                    <p className="text-[11px] text-text-secondary mb-20">Upload your CV, certificates, contracts and more.</p>
                    <button onClick={() => navigate(uploadLink)} className="btn btn-primary">
                        <IconUpload size={14} /> Upload Document
                    </button>
                </div>
            ) : (
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    {docs.map((doc, i) => {
                        const VisIcon    = VISIBILITY_ICONS[doc.visibility] || IconLock;
                        const expired    = doc.is_expired;
                        const expiringSoon = !expired && doc.days_until_expiry != null && doc.days_until_expiry <= 30;
                        return (
                            <div key={doc.id} className={`flex items-center gap-12 px-15 py-10 ${i > 0 ? 'border-t border-border' : ''}`}>
                                <div className="w-[34px] h-[34px] rounded-lg bg-bg-light flex items-center justify-center flex-shrink-0">
                                    <IconFile size={16} className={MIME_ICON_COLOR[doc.mime_type] || 'text-text-secondary'} />
                                </div>

                                <div className="flex-grow min-w-0">
                                    <div className="flex items-center gap-6 flex-wrap">
                                        <p className="text-sm font-semibold text-text-primary truncate">{doc.title}</p>
                                        {expired && (
                                            <span className="text-[10px] font-bold uppercase tracking-widest px-5 py-1 rounded-full bg-danger/10 text-danger">Expired</span>
                                        )}
                                        {expiringSoon && (
                                            <span className="text-[10px] font-bold uppercase tracking-widest px-5 py-1 rounded-full bg-warning/10 text-warning">
                                                Expires in {doc.days_until_expiry}d
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-10 mt-2 flex-wrap">
                                        <span className="text-xs text-text-secondary">{doc.category_display || categoryLabel(doc.category)}</span>
                                        <span className="text-text-secondary/30">·</span>
                                        <span className="text-xs text-text-secondary">{doc.file_size_display || formatSize(doc.file_size)}</span>
                                        <span className="text-text-secondary/30">·</span>
                                        <span className="inline-flex items-center gap-3 text-xs text-text-secondary">
                                            <VisIcon size={11} /> {doc.visibility_display || doc.visibility}
                                        </span>
                                        {doc.expires_on && (
                                            <>
                                                <span className="text-text-secondary/30">·</span>
                                                <span className={`text-xs ${expired ? 'text-danger' : expiringSoon ? 'text-warning' : 'text-text-secondary'}`}>
                                                    Expires {formatDate(doc.expires_on)}
                                                </span>
                                            </>
                                        )}
                                        {isHR && doc.uploaded_by_name && (
                                            <>
                                                <span className="text-text-secondary/30">·</span>
                                                <span className="text-xs text-text-secondary">by {doc.uploaded_by_name}</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 flex-shrink-0">
                                    <button
                                        onClick={() => handleDownload(doc)}
                                        className="w-28 h-28 rounded-lg flex items-center justify-center text-text-secondary hover:bg-primary/10 hover:text-primary transition-colors"
                                        title="Download"
                                    >
                                        <IconDownload size={14} />
                                    </button>
                                    <button
                                        onClick={() => navigate(`/documents/${doc.id}/edit`)}
                                        className="w-28 h-28 rounded-lg flex items-center justify-center text-text-secondary hover:bg-info/10 hover:text-info transition-colors"
                                        title="Edit"
                                    >
                                        <IconEdit size={14} />
                                    </button>
                                    <button
                                        onClick={() => setAccessLogDoc(doc)}
                                        className="w-28 h-28 rounded-lg flex items-center justify-center text-text-secondary hover:bg-bg-light transition-colors"
                                        title="Access Log"
                                    >
                                        <IconHistory size={14} />
                                    </button>
                                    <button
                                        onClick={() => setConfirmDelete(doc)}
                                        className="w-28 h-28 rounded-lg flex items-center justify-center text-text-secondary hover:bg-danger/10 hover:text-danger transition-colors"
                                        title="Delete"
                                    >
                                        <IconTrash size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Access log */}
            {accessLogDoc && (
                <AccessLogModal doc={accessLogDoc} onClose={() => setAccessLogDoc(null)} />
            )}

            {/* Delete confirm */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-15">
                    <div className="bg-bg-dark rounded-xl border border-border shadow-xl w-full max-w-sm p-20">
                        <h3 className="text-[14px] font-bold text-text-primary mb-6">Delete Document</h3>
                        <p className="text-[12px] text-text-secondary mb-20">
                            Are you sure you want to permanently delete <span className="font-semibold text-text-primary">"{confirmDelete.title}"</span>? This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-10">
                            <button onClick={() => setConfirmDelete(null)} className="btn btn-outline-secondary">Cancel</button>
                            <button onClick={handleDelete} disabled={deleteDoc.isPending} className="btn btn-danger">
                                {deleteDoc.isPending ? <IconLoader className="animate-spin" size={14} /> : <IconTrash size={14} />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
